import React from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate, useParams } from "react-router-dom";

import Hero from "../designSystem/Hero";
import "../../css/pages/homePage.css";
import "../../css/components/BlogsCard.css";
import HeroImage from "../../assets/images/Blog1.jpg";

import { blogPosts } from "../../data/blogPosts";

const SITE_URL = "https://fgwealthadvisory.co.uk";

const BlogPostPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();

  const post = blogPosts.find((item) => item.link === `/blog/${slug}`);

  if (!post) {
    return (
      <div className="home-page">
        <section className="page-section">
          <div className="page-section-inner">
            <h2 className="secondary">Blog not found</h2>
            <p className="body primary">
              Please go back to the blog page and select an article.
            </p>
            <button onClick={() => navigate("/blog")}>Back to blogs</button>
          </div>
        </section>
      </div>
    );
  }

  const pageUrl = `${SITE_URL}${post.link}`;
  const imageUrl = `${SITE_URL}${post.image || "/images/blog-preview.jpg"}`;
  const description =
    post.excerpt ||
    "Read the latest financial planning insights from FG Wealth Advisory.";

  return (
    <div className="home-page">
      <Helmet>
        <title>{post.title} | FG Wealth Advisory</title>
        <meta name="description" content={description} />

        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={description} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:image" content={imageUrl} />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.title} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={imageUrl} />
      </Helmet>

      <Hero title={post.title} subtitle="BLOG" backgroundImage={HeroImage} />

      <section className="page-section">
        <div className="page-section-inner">
          {post.date && (
            <>
              <p className="blog-post-content body primary">{post.date}</p>
              <div className="divider primary align-left" />
            </>
          )}

          <div className="blog-post-content body primary">
            {Array.isArray(post.sections)
              ? post.sections.map((section, index) => {
                  if (section.type === "heading") {
                    return (
                      <h2 key={index} className="blog-post-heading secondary">
                        {section.text}
                      </h2>
                    );
                  }

                  if (section.type === "subheading") {
                    return (
                      <h3
                        key={index}
                        className="blog-post-subheading secondary"
                      >
                        {section.text}
                      </h3>
                    );
                  }

                  if (section.type === "numberedList") {
                    return (
                      <ol key={index} className="blog-post-numbered-list">
                        {section.items.map((item, itemIndex) => (
                          <li key={itemIndex}>{item}</li>
                        ))}
                      </ol>
                    );
                  }

                  if (section.type === "bulletList") {
                    return (
                      <ul key={index} className="blog-post-bullet-list">
                        {section.items.map((item, itemIndex) => (
                          <li key={itemIndex}>{item}</li>
                        ))}
                      </ul>
                    );
                  }

                  if (section.type === "faq") {
                    return (
                      <div key={index} className="blog-post-faq">
                        {section.items.map((item, itemIndex) => (
                          <div key={itemIndex} className="blog-post-faq-item">
                            <h3 className="blog-post-faq-question secondary">
                              {item.question}
                            </h3>
                            <p>{item.answer}</p>
                          </div>
                        ))}
                      </div>
                    );
                  }

                  if (section.type === "disclaimer") {
                    return (
                      <p key={index} className="blog-post-disclaimer">
                        {section.text}
                      </p>
                    );
                  }

                  return <p key={index}>{section.text}</p>;
                })
              : post.content?.split("\n").map((paragraph, index) => {
                  const trimmed = paragraph.trim();
                  if (!trimmed) return null;

                  return <p key={index}>{trimmed}</p>;
                })}

            {Array.isArray(post.sources) && post.sources.length > 0 && (
              <div className="blog-post-sources">
                <h3 className="secondary">Sources</h3>
                <ol>
                  {post.sources.map((source, index) => (
                    <li key={index}>{source}</li>
                  ))}
                </ol>
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="investment-note-section">
        <div className="home-container investment-note body">
          <strong>Please Note:</strong> The value of pensions and investments
          and the income they produce can fall as well as rise. You may get back
          less than you invested. This information is for general guidance only
          and does not constitute personal financial advice.
        </div>
      </section>
    </div>
  );
};

export default BlogPostPage;
